// API calls for user management

export interface User {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
  wallet_address: string | null;
  role: 'admin' | 'client';
  created_at: string;
  updated_at: string;
}

export interface CreateUserRequest {
  firstName: string;
  lastName: string;
  email: string;
  walletAddress?: string;
  role: 'admin' | 'client';
}

export interface UpdateUserRequest {
  firstName?: string;
  lastName?: string;
  email?: string;
  walletAddress?: string;
  role?: 'admin' | 'client';
}

const getAuthHeaders = () => {
  const token = localStorage.getItem('authToken');
  return {
    'Content-Type': 'application/json',
    'Authorization': token ? `Bearer ${token}` : '',
  };
};

export const getAllUsers = async (): Promise<{ users: User[]; count: number }> => {
  const response = await fetch('/api/users', {
    method: 'GET',
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to fetch users');
  }

  return response.json();
};

export const getUserById = async (id: number): Promise<{ user: User }> => {
  const response = await fetch(`/api/users/${id}`, {
    method: 'GET',
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to fetch user');
  }

  return response.json();
};

export const createUser = async (userData: CreateUserRequest): Promise<{ user: User; message: string }> => {
  const response = await fetch('/api/users', {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(userData),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to create user');
  }

  return response.json();
};

export const updateUser = async (id: number, userData: UpdateUserRequest): Promise<{ user: User; message: string }> => {
  const response = await fetch(`/api/users/${id}`, {
    method: 'PUT',
    headers: getAuthHeaders(),
    body: JSON.stringify(userData),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to update user');
  }

  return response.json();
};

export const deleteUser = async (id: number): Promise<{ message: string }> => {
  const response = await fetch(`/api/users/${id}`, {
    method: 'DELETE',
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to delete user');
  }

  return response.json();
};
